import { IService } from "./services/typing";
import { IServiceVideoChat } from "./services/video-chat";
import { IServiceTextChat } from "./services/text-chat";

export type RemoServicesCatalog = {
    videoChat: IServiceVideoChat;
    textChat: IServiceTextChat;
};

export type RemoServiceID = Extract<keyof RemoServicesCatalog, string>;

export type RemoServicesInstance<T extends RemoServiceID> = RemoServicesCatalog[T];

export type RemoServicesCreator<T extends RemoServiceID> = () => Promise<
    RemoServicesCatalog[T] | null
>;

export type RemoServicesCreatorCatalog = {
    [K in RemoServiceID]: RemoServicesCreator<K>;
};

declare global {
    interface Window {
        __ReMoSeRvIcEs?: RemoServices;
    }
}

/**
 * 服务管理基类，用于注册、注销和请求各种服务
 */
export class RemoServices {
    private static instance: RemoServices | null = null;
    /**
     * 获取RemoServices单例实例
     * @returns {RemoServices} 服务管理实例
     */
    public static getInstance(): RemoServices {
        if (!RemoServices.instance) {
            RemoServices.instance = new RemoServices();
            window.__ReMoSeRvIcEs = RemoServices.instance;
        }
        return RemoServices.instance;
    }

    private registry = new Map<RemoServiceID, () => Promise<IService | null>>();
    private services = new Map<RemoServiceID, Promise<IService | null>>();

    /**
     * 私有构造函数，防止外部实例化
     */
    private constructor() {
        // 防止被实例化
    }

    /**
     * 注册一个或多个服务
     * @template RemoServiceID - 服务ID类型
     * @param {RemoServiceID | RemoServiceID[]} name - 服务ID或ID数组
     * @param {RemoServicesCreatorCatalog[RemoServiceID]} serviceCreator - 服务创建函数
     * @throws {Error} 如果服务已注册则抛出错误
     */
    public register<T extends RemoServiceID>(
        name: T | T[],
        serviceCreator: RemoServicesCreatorCatalog[T],
    ): void {
        const names = Array.isArray(name) ? name : [name];
        for (const name of names) {
            if (this.isRegistered(name)) {
                console.warn("服务已经被注册过了", name);
                continue;
            }
            this.registry.set(name, serviceCreator);
        }
    }

    /**
     * 注销服务
     * @template RemoServiceID - 服务ID类型
     * @param {RemoServiceID} name - 要注销的服务ID
     * @returns {Promise<boolean>} 是否成功注销
     */
    public async unregister<T extends RemoServiceID>(name: T): Promise<boolean> {
        if (this.isRegistered(name)) {
            this.registry.delete(name);
            await this.shutdownService(name);
            return true;
        }
        return false;
    }

    /**
     * 请求服务实例
     * @template RemoServiceID - 服务ID类型
     * @param {RemoServiceID} name - 要请求的服务ID
     * @param {boolean} [keepReference=true] - 是否保留服务引用
     * @returns {Promise<RemoServicesInstance<RemoServiceID> | null>} 服务实例或null
     */
    public async requestService<T extends RemoServiceID>(
        name: T,
        keepReference = true,
    ): Promise<RemoServicesInstance<T> | null> {
        let service = this.services.get(name) || null;
        // eslint-disable-next-line no-negated-condition
        if (!service) {
            console.log("[DEBUG LOG] 请求service重新创建实例", name, keepReference);
            const creator = this.registry.get(name);
            if (creator) {
                service = creator();
                if (keepReference) {
                    this.services.set(name, service);
                    console.log("[DEBUG LOG] 请求service保留实例", service);
                }
            }
        } else {
            console.log("[DEBUG LOG] 请求service存在实例", name);
        }
        return service as Promise<RemoServicesInstance<T> | null>;
    }

    /**
     * 关闭指定服务
     * @template RemoServiceID - 服务ID类型
     * @param {RemoServiceID} name - 要关闭的服务ID
     * @returns {Promise<boolean>} 是否成功关闭
     */
    public async shutdownService<T extends RemoServiceID = RemoServiceID>(
        name: T,
    ): Promise<boolean> {
        const pService = this.services.get(name);
        if (pService) {
            this.services.delete(name);
            const service = await pService;
            try {
                await service?.destroy?.();
            } catch (e) {
                if (process.env.NODE_ENV !== "production") {
                    console.error(e);
                }
            }
            return true;
        }
        return false;
    }

    /**
     * 关闭所有服务
     * @returns {Promise<void>}
     */
    public async shutdownAllServices(): Promise<void> {
        const shutdownPromises = Array.from(this.services.entries()).map(
            async ([name, servicePromise]) => {
                try {
                    const service = await servicePromise;
                    await service?.destroy?.();
                } catch (error) {
                    if (process.env.NODE_ENV !== "production") {
                        console.error(`Service error [${name}]:`, error);
                    }
                }
            },
        );
        await Promise.all(shutdownPromises);
        this.services.clear();
    }

    /**
     * 检查服务是否已注册
     * @param {RemoServiceID} name - 服务ID
     * @returns {boolean} 是否已注册
     */
    public isRegistered(name: RemoServiceID): boolean {
        return this.registry.has(name);
    }

    /**
     * 检查服务实例是否已创建
     * @param {RemoServiceID} name - 服务ID
     * @returns {boolean} 是否已创建
     */
    public isCreated(name: RemoServiceID): boolean {
        return this.services.has(name);
    }
}
